// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
import { ExclamationCircle } from 'flowbite-react-icons/outline'
import { type AnimationEvent, useEffect, useState } from 'react'
import { countUpValue } from './clinicalSummaryGenerationReveal'
import { PILL_SHELL_CLASS, WARNING_PILL_TONE_CLASS } from './clinicalSummaryPillStyles'

export type PhenotypeBadgeTone = 'warning' | 'success' | 'gray'

export interface PhenotypeBadgeCountUp {
  id: number
  from: number
  to: number
  durationMs: number
}

interface ClinicalSummaryPhenotypeBadgeProps {
  generating: boolean
  label: string
  tone: PhenotypeBadgeTone
  /** Ticks the count up to `to` after a generation, then settles on `label` with a pop. */
  countUp: PhenotypeBadgeCountUp | null
}

const DOTS_EVERY_MS = 400

const toneClass: Record<PhenotypeBadgeTone | 'brand', string> = {
  brand: 'border-brand-100 dark:border-brand-800 bg-background-brand-soft dark:bg-gray-800 text-brand-800 dark:text-brand-400 inset-ring-brand-100',
  warning: WARNING_PILL_TONE_CLASS,
  success: 'border-success-100 dark:border-success-700 bg-success-50 dark:bg-gray-900 text-success-700 dark:text-success-100 inset-ring-success-100',
  gray: 'border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 text-text-heading dark:text-white inset-ring-border-base',
}

// helix-exception: the sparkle glyph has no flowbite-react-icons outline equivalent, so it stays an inline SVG (F-13)
function SparkleIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="size-3 shrink-0"
    >
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    </svg>
  )
}

/** One, two, three dots, then back to one, while `active`. */
function useCyclingDots(active: boolean): string {
  const [dots, setDots] = useState(1)
  useEffect(() => {
    if (!active) return
    setDots(1)
    const timer = setInterval(() => setDots((count) => (count % 3) + 1), DOTS_EVERY_MS)
    return () => clearInterval(timer)
  }, [active])
  return '.'.repeat(dots)
}

function useCountUp(countUp: PhenotypeBadgeCountUp | null): { value: number; settled: boolean } | null {
  const id = countUp?.id ?? null
  const from = countUp?.from ?? 0
  const to = countUp?.to ?? 0
  const durationMs = countUp?.durationMs ?? 0
  const [progress, setProgress] = useState<{ id: number; value: number; settled: boolean } | null>(null)

  useEffect(() => {
    if (id === null) return
    const start = performance.now()
    let frame = requestAnimationFrame(function tick(now) {
      const elapsed = now - start
      const settled = elapsed >= durationMs
      setProgress({ id, value: countUpValue(from, to, elapsed, durationMs), settled })
      if (!settled) frame = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frame)
  }, [id, from, to, durationMs])

  if (id === null) return null
  // Until the first frame lands, show the starting count rather than the final label.
  return progress?.id === id ? progress : { value: from, settled: false }
}

/**
 * Keeps the pop on until it has played: the caller's reveal can end, and drop `countUp`, while the
 * pop is still running, because the count-up only starts once the badge renders.
 */
function usePop(settledId: number | null, generating: boolean) {
  const [poppingId, setPoppingId] = useState<number | null>(null)
  useEffect(() => {
    if (settledId !== null) setPoppingId(settledId)
  }, [settledId])
  useEffect(() => {
    if (generating) setPoppingId(null)
  }, [generating])
  return {
    popping: !generating && (settledId !== null || poppingId !== null),
    onAnimationEnd: (event: AnimationEvent<HTMLSpanElement>) => {
      if (event.target === event.currentTarget) setPoppingId(null)
    },
  }
}

export function ClinicalSummaryPhenotypeBadge({ generating, label, tone, countUp }: ClinicalSummaryPhenotypeBadgeProps) {
  const dots = useCyclingDots(generating)
  const count = useCountUp(generating ? null : countUp)
  const counting = count !== null && !count.settled
  const shownTone = generating || counting ? 'brand' : tone
  const pop = usePop(count?.settled && countUp ? countUp.id : null, generating)

  return (
    <span
      className={`${PILL_SHELL_CLASS} ${toneClass[shownTone]}${
        pop.popping && !counting ? ' motion-safe:animate-[clinical-summary-pop_0.32s_ease-out]' : ''
      }`}
      onAnimationEnd={pop.onAnimationEnd}
      aria-live="polite"
      aria-busy={counting}
      data-testid="clinical-summary-phenotypes-badge"
    >
      {generating ? (
        <>
          <SparkleIcon />
          <span>
            Finding phenotypes<span aria-hidden>{dots}</span>
          </span>
        </>
      ) : counting ? (
        `${count.value} found`
      ) : (
        <>
          {shownTone === 'warning' ? <ExclamationCircle className="size-3 shrink-0" aria-hidden /> : null}
          {shownTone === 'success' ? (
            <span
              aria-hidden
              className="inline-flex size-3 items-center justify-center rounded-full border border-current text-[8px] leading-none"
            >
              ✓
            </span>
          ) : null}
          {label}
        </>
      )}
    </span>
  )
}
